import { LevelModel, RoomModel } from '../types/protocols';
import { formatLevelNumberPlain } from './levelDisplayName';

/**
 * Pure helpers for rooms that span several levels (multi-level rooms plan D2).
 * `levelKey` is the room's primary level; `levelKeys` lists every level when
 * there are two or more. Only drawings, drawing-replacement relocation and
 * unit level derivation read these — text lists, pickers, breadcrumbs, counts
 * and area sums keep using `levelKey`.
 */

type RoomLevels = Pick<RoomModel, 'levelKey' | 'levelKeys'>;

const isNonEmptyKey = (key: unknown): key is string => typeof key === 'string' && key.trim().length > 0;

/**
 * Every level the room is on: the keys of `levelKeys`, or `[levelKey]` when
 * it is absent. Keys of levels that no longer exist are dropped when `levels`
 * is given. Order: the map's key order, with `levelKey` first.
 */
export const roomLevelKeys = (
    room: RoomLevels,
    levels?: { [key: string]: LevelModel }
): string[] => {
    const fromMap = Object.entries(room.levelKeys || {})
        .filter(([key, flag]) => flag === true && isNonEmptyKey(key))
        .map(([key]) => key);
    const keys = fromMap.length > 0 ? fromMap : isNonEmptyKey(room.levelKey) ? [room.levelKey] : [];
    const ordered = isNonEmptyKey(room.levelKey) && keys.includes(room.levelKey)
        ? [room.levelKey, ...keys.filter(key => key !== room.levelKey)]
        : keys;
    return levels ? ordered.filter(key => !!levels[key]) : ordered;
};

/** True when the room is on `levelKey`, on any of its levels. */
export const roomIsOnLevel = (room: RoomLevels, levelKey: string): boolean =>
    roomLevelKeys(room).includes(levelKey);

/**
 * The primary level of a set of level keys (plan D1): the lowest by level
 * `number`, then by key. Null for an empty set or when none of the keys is a
 * level.
 */
export const primaryLevelKey = (
    levelKeys: Iterable<string>,
    levels: { [key: string]: Pick<LevelModel, 'number'> } | undefined
): string | null => {
    const existing = Array.from(levelKeys).filter(key => !!levels?.[key]);
    if (existing.length === 0) return null;
    return existing.sort((a, b) => {
        const byNumber = levels![a].number - levels![b].number;
        return byNumber !== 0 ? byNumber : a.localeCompare(b);
    })[0];
};

/**
 * The room's levels as text: "Plan 01" for one level, "Plan 01–04" for a
 * span. Levels that no longer exist contribute nothing; empty string when
 * none is left.
 */
export const roomLevelSpanLabel = (
    room: RoomLevels,
    levels: { [key: string]: LevelModel } | undefined
): string => {
    if (!levels) return '';
    const numbers = roomLevelKeys(room, levels)
        .map(key => levels[key].number)
        .sort((a, b) => a - b);
    if (numbers.length === 0) return '';
    const first = formatLevelNumberPlain(numbers[0]);
    const last = formatLevelNumberPlain(numbers[numbers.length - 1]);
    return numbers.length === 1 || first === last ? `Plan ${first}` : `Plan ${first}–${last}`;
};

/** True when the room spans two or more existing levels. */
export const isMultiLevelRoom = (room: RoomLevels, levels?: { [key: string]: LevelModel }): boolean =>
    roomLevelKeys(room, levels).length > 1;
